import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LoanListItem } from '@app/shared';
import { FIND_COLUMN_IDS, filterableFor, cols, type Column } from './columns';
import {
  applyFilters, applyQuickSearch, compareBy, type Filter, type SortSpec,
} from './filterModel';
import { patchViewState, readViewState } from './viewState';

/**
 * Filter + sort state for one table, plus the derived row set.
 *
 * `defaultSort` is the resting order when the user hasn't clicked a header — it
 * lets the SA queue open in work-queue order (soonest Next Action first) while
 * still allowing any column to be clicked to override it.
 *
 * It's declared as a column + direction rather than a comparator so the UI can
 * *show* it. A bare comparator sorts correctly but is invisible: nothing can
 * render an arrow for an ordering it can't name.
 */
export function useTableView(
  rows: LoanListItem[],
  columnIds: readonly string[],
  defaultSort?: SortSpec,
  /**
   * Filters the table opens with — e.g. "Analyst equals <me>". They're ordinary
   * chips once created: editable, removable, and replaceable with anyone else's
   * name, which is the point of expressing the default as a filter at all.
   */
  initialFilters?: Filter[],
  /** Breaks ties within the default order — e.g. borrower name within a date. */
  defaultTiebreak?: (a: LoanListItem, b: LoanListItem) => number,
  /**
   * Name under which to remember this view in sessionStorage, so navigating to a
   * loan and back doesn't discard the user's filters and ordering. Omit and the
   * table keeps its previous in-memory-only behaviour.
   */
  persistAs?: string,
  /**
   * OID of the signed-in user, stamped onto the stored view so a view saved by a
   * different identity is not mistaken for this one's. May be undefined on the
   * first renders, before sign-in resolves.
   */
  identity?: string,
) {
  const columns = useMemo(() => cols(columnIds), [columnIds]);
  const fields = useMemo(() => filterableFor(columnIds), [columnIds]);
  const findColumns = useMemo(() => cols(FIND_COLUMN_IDS), []);

  const columnOf = useCallback((id: string) => fields.find(c => c.id === id), [fields]);

  /**
   * The stored view, read once at mount.
   *
   * Read through a ref so it happens exactly once per mount rather than on every
   * render, and before the first paint — restoring in an effect would show the
   * default view for a frame and then visibly rearrange it.
   */
  const restoredRef = useRef<ReturnType<typeof readViewState> | null>(null);
  if (restoredRef.current === null) {
    restoredRef.current = persistAs ? readViewState(persistAs, columnOf) : {};
  }
  const restored = restoredRef.current;

  const [filters, setFilters] = useState<Filter[]>(restored.filters ?? initialFilters ?? []);
  const [quick, setQuick] = useState(restored.quick ?? '');
  const [sort, setSort] = useState<SortSpec | null>(restored.sort ?? null);
  /**
   * The applied "Find a loan" term, '' when not searching.
   *
   * This is a separate concept from `filters` on purpose. Filters narrow the
   * set you're working in; a find widens to the whole portfolio. Reviewing your
   * assignments and locating someone else's loan are two different tasks, and
   * one control serving both is what made the second one awkward.
   */
  const [findAll, setFindAll] = useState(restored.findAll ?? '');

  /**
   * Whether this mount adopted a stored *filter* set. The page needs to know: its
   * default-filter seeder fires once `me` arrives, which is normally after the
   * first paint, and would otherwise overwrite the restored chips a beat after
   * the user has already seen them.
   *
   * Keyed on `filters` specifically, not on the blob being non-empty. The page
   * shares this blob for its page number and expanded rows, and either of those
   * arriving alone says nothing about whether the user's own filters were ever
   * stored — reading it as "restored" would suppress the default chip on the
   * strength of someone having turned to page 2.
   */
  const restoredView = restored.filters !== undefined;

  /**
   * The identity the restored filters were saved under, or undefined if none were.
   * The page compares this with the current user: an assignment chip built for
   * somebody else matches nothing, so the defaults have to win instead.
   */
  const restoredIdentity = restoredView ? restored.identity : undefined;

  // Write back whenever any of it changes. An effect rather than a write inside
  // each setter, so it cannot be forgotten when a new setter is added, and so a
  // burst of changes in one render costs one write.
  //
  // The mount pass is skipped, so a view nobody has touched leaves nothing
  // behind. Without that, merely opening the page stored an empty filter set,
  // and a user who clicked into a loan before sign-in had finished resolving
  // came back to a "restored" empty view — which suppresses the default
  // assignment chip that was still waiting on `me` to be built.
  const mounted = useRef(false);
  useEffect(() => {
    if (!persistAs) return;
    if (!mounted.current) { mounted.current = true; return; }
    patchViewState(persistAs, { filters, quick, findAll, sort, ...(identity ? { identity } : {}) });
  }, [persistAs, identity, filters, quick, findAll, sort]);

  /**
   * Cycle a column's sort: ascending → descending → back to the table's default.
   *
   * Compares against the *effective* order, not just an explicit click. On load
   * `sort` is null while the table is really sorted by `defaultSort`, so keying
   * off `sort` alone made the first click on the default column re-apply the
   * direction it already had — it looked like the arrow refused to flip.
   *
   * A column that is itself the default therefore cycles in two steps rather
   * than three: descending, then back to default (which is its ascending form).
   */
  const toggleSort = useCallback((id: string) => {
    setSort(prev => {
      const current = prev ?? defaultSort ?? null;
      if (current?.id !== id) return { id, dir: 'asc' };
      return current.dir === 'asc' ? { id, dir: 'desc' } : null;
    });
  }, [defaultSort]);

  // What the table is *actually* ordered by, clicked or not. The default is a
  // real ordering, so it gets shown like one.
  const effectiveSort = sort ?? defaultSort ?? null;

  // Third click on a header clears back to the default order rather than
  // cycling asc/desc forever — there's no other way back to the resting sort.
  const sortIndicator = useCallback(
    (id: string) => effectiveSort?.id === id ? (effectiveSort.dir === 'asc' ? ' ▲' : ' ▼') : '',
    [effectiveSort],
  );

  /**
   * The effective order in words, e.g. "Next Action ↑".
   *
   * Needed because the default may order by a column this table doesn't show —
   * the analyst grid opens newest-first on Created, which has no header to carry
   * an arrow. Without this the opening order is simply invisible.
   */
  const sortLabel = useMemo(() => {
    if (!effectiveSort) return null;
    const c = columnOf(effectiveSort.id);
    if (!c) return null;
    return `${c.header} ${effectiveSort.dir === 'asc' ? '↑' : '↓'}`;
  }, [effectiveSort, columnOf]);

  const view = useMemo(() => {
    // A find is the outer scope: it searches the whole portfolio, and anything
    // the user adds afterwards narrows within its results.
    let out = findAll ? applyQuickSearch(rows, findAll, findColumns) : rows;
    out = applyFilters(out, filters, columnOf);
    out = applyQuickSearch(out, quick, columns);

    const sorted = [...out];
    const c = effectiveSort && columnOf(effectiveSort.id);
    if (c && effectiveSort) {
      const compare = compareBy(c, effectiveSort.dir);
      // The tiebreak applies to every ordering, not just the resting one.
      // Applying it only to the default meant rows sharing a sort key visibly
      // reshuffled the moment you clicked the column they were already sorted
      // by — the same order, arrived at two different ways.
      sorted.sort(defaultTiebreak
        ? (a, b) => compare(a, b) || defaultTiebreak(a, b)
        : compare);
    }
    return sorted;
  }, [
    rows, findAll, findColumns, filters, quick, columns, columnOf,
    effectiveSort, defaultTiebreak,
  ]);

  /** Drop any clicked ordering, returning the table to `defaultSort`. */
  const resetSort = useCallback(() => setSort(null), []);

  return {
    columns, fields, filters, setFilters, quick, setQuick,
    findAll, setFindAll,
    sort, effectiveSort, toggleSort, resetSort, sortIndicator, sortLabel, view,
    restoredView, restoredIdentity,
    activeCount: filters.length + (quick.trim() ? 1 : 0),
  } as const;
}

/** Header cells for a set of columns — every column is sortable. */
export type HeaderProps = {
  columns: Column[];
  onSort: (id: string) => void;
  indicator: (id: string) => string;
};
